import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Logo } from "@/components/Logo";
import { ProductIcon } from "@/components/ProductIcon";
import { QuestionContextBanner } from "@/components/wizard/QuestionContextBanner";
import { WizardOption } from "@/components/wizard/WizardOption";
import { WizardProgress } from "@/components/wizard/WizardProgress";
import { WizardReview } from "@/components/wizard/WizardReview";
import { useApp } from "@/context/AppContext";
import { isAnswered, pruneAnswers } from "@/engine/answers";
import {
  licenseOptions,
  nextVisibleIndex,
  previousVisibleIndex,
  productsForLocale,
  rwVersionOptions,
  visibleQuestionIds,
} from "@/engine/questions";
import {
  appliesToPlatforms,
  buildProgressSegments,
  progressPosition,
  questionContext,
  questionKey,
  stepHint,
} from "@/engine/wizardUi";
import type { QuestionOption, WizardAnswers } from "@/types";
import { trackEvent } from "@/analytics";

function ArrowLeftIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-4 h-4"
      aria-hidden="true"
    >
      <path d="m12 19-7-7 7-7" />
      <path d="M19 12H5" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-4 h-4"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

export function WizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { config, locale, answers, setAnswers } = useApp();
  const step = params.get("step") ?? "products";
  const visible = visibleQuestionIds(answers);
  const products = productsForLocale(config.edition.wizard, locale);
  const isReview = step === "review";
  const isMultiple = [
    "products",
    "rw_version",
    "mv_version",
    "mv_assist_platforms",
    "tasks_os",
    "tasks_browser",
  ].includes(step);
  const currentValue = answers[step];
  const canContinue = isReview || isAnswered(currentValue);
  const context = questionContext(step, answers);
  const hintKey = stepHint(step, answers);
  const platforms = appliesToPlatforms(step, answers);

  const currentOptions = useMemo((): QuestionOption[] => {
    if (step === "products") {
      return products.map((product) => ({
        value: product.id,
        labelKey: `wizard.steps.products.options.${product.id}`,
        icon: product.icon,
        comingSoon: product.comingSoon,
      }));
    }
    if (step === "rw_license") return licenseOptions(answers);
    if (step === "rw_version") return rwVersionOptions(answers);
    if (step === "mv_version") {
      return [
        { value: "assist", labelKey: "wizard.steps.mv_version.options.assist", icon: "mindview_assist" },
        { value: "mac", labelKey: "wizard.steps.mv_version.options.mac", icon: "macos" },
        { value: "online", labelKey: "wizard.steps.mv_version.options.online", icon: "mindview_online" },
        { value: "win", labelKey: "wizard.steps.mv_version.options.win", icon: "windows" },
      ];
    }
    if (step === "mv_assist_platforms") {
      return [
        {
          value: "android",
          labelKey: "wizard.steps.mv_assist_platforms.options.android",
          icon: "android",
        },
        {
          value: "ios",
          labelKey: "wizard.steps.mv_assist_platforms.options.ios",
          icon: "ios",
        },
      ];
    }
    if (step === "tasks_os") {
      return [
        { value: "chromeos", labelKey: "wizard.steps.tasks_os.options.chromeos", icon: "browser" },
        { value: "mac", labelKey: "wizard.steps.tasks_os.options.mac", icon: "macos" },
        { value: "win", labelKey: "wizard.steps.tasks_os.options.win", icon: "windows" },
      ];
    }
    if (step === "tasks_intune") {
      return [
        { value: "yes", labelKey: "wizard.steps.tasks_intune.options.yes" },
        { value: "no", labelKey: "wizard.steps.tasks_intune.options.no" },
      ];
    }
    if (step === "offline") {
      return [
        { value: "no", labelKey: "wizard.steps.offline.options.no" },
        {
          value: "yes",
          labelKey: "wizard.steps.offline.options.yes.label",
          tooltipKey: "wizard.steps.offline.options.yes.tooltip",
        },
      ];
    }
    if (step === "network_restricted") {
      return [
        {
          value: "yes",
          labelKey: "wizard.steps.network_restricted.options.yes.label",
          tooltipKey: "wizard.steps.network_restricted.options.yes.tooltip",
        },
        { value: "no", labelKey: "wizard.steps.network_restricted.options.no" },
      ];
    }
    if (step === "use_on_webpages") {
      return [
        {
          value: "yes",
          labelKey: "wizard.steps.use_on_webpages.options.yes.label",
          tooltipKey: "wizard.steps.use_on_webpages.options.yes.tooltip",
        },
        { value: "no", labelKey: "wizard.steps.use_on_webpages.options.no" },
      ];
    }
    if (step === "login_method") {
      return [
        {
          value: "google_microsoft",
          labelKey: "wizard.steps.login_method.options.google_microsoft",
          icons: ["google", "microsoft"],
        },
        {
          value: "auto",
          labelKey: "wizard.steps.login_method.options.auto.label",
          tooltipRwKey: "wizard.steps.login_method.options.auto.tooltipRw",
          tooltipMvKey: "wizard.steps.login_method.options.auto.tooltipMv",
        },
      ];
    }
    if (step === "mv_win_restrict_online_services") {
      return [
        {
          value: "yes",
          labelKey: "wizard.steps.mv_win_restrict_online_services.options.yes.label",
          tooltipKey: "wizard.steps.mv_win_restrict_online_services.options.yes.tooltip",
        },
        { value: "no", labelKey: "wizard.steps.mv_win_restrict_online_services.options.no" },
      ];
    }
    if (step === "tasks_start_menu") {
      return [
        { value: "yes", labelKey: "wizard.steps.tasks_start_menu.options.yes" },
        { value: "no", labelKey: "wizard.steps.tasks_start_menu.options.no" },
      ];
    }
    if (step === "tasks_browser") {
      return [
        { value: "chrome", labelKey: "wizard.steps.tasks_browser.options.chrome", icon: "chrome" },
        { value: "edge", labelKey: "wizard.steps.tasks_browser.options.edge", icon: "edge" },
        { value: "safari", labelKey: "wizard.steps.tasks_browser.options.safari", icon: "safari" },
      ];
    }
    return [];
  }, [answers, products, step]);

  const formatAnswer = useCallback(
    (id: string): string => {
      const value = answers[id];
      const labelFor = (item: string) => {
        const nested = t(`wizard.steps.${id}.options.${item}.label`);
        if (nested && !nested.startsWith("wizard.steps")) return nested;
        const flat = t(`wizard.steps.${id}.options.${item}`);
        return flat.startsWith("wizard.steps") ? item : flat;
      };
      if (Array.isArray(value)) return value.map(labelFor).join(", ");
      if (!value) return "";
      return labelFor(value);
    },
    [answers, t],
  );

  const progressSegments = useMemo(
    () =>
      buildProgressSegments(
        step,
        answers,
        formatAnswer,
        t("wizard.customerType.workplace.label"),
      ),
    [answers, formatAnswer, step, t],
  );
  const { current: progressCurrent, total: progressTotal } = progressPosition(step, visible);

  const updateAnswer = (value: string) => {
    const next: WizardAnswers = { ...answers };
    if (isMultiple) {
      const current = Array.isArray(currentValue) ? [...currentValue] : [];
      next[step] = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
    } else {
      next[step] = value;
    }
    setAnswers(pruneAnswers(next, visibleQuestionIds(next)));
  };

  const goNext = () => {
    if (isReview) {
      trackEvent("report_generated", { products: answers.products });
      navigate("/report");
      return;
    }
    const next = nextVisibleIndex(step, answers);
    if (next === "review") {
      setParams({ step: "review" });
      return;
    }
    setParams({ step: next });
  };

  const goBack = () => {
    if (isReview) {
      const last = visible[visible.length - 1];
      setParams(last ? { step: last } : {});
      return;
    }
    const previous = previousVisibleIndex(step, answers);
    if (previous === "home") {
      navigate("/");
      return;
    }
    setParams({ step: previous });
  };

  const navigateProgress = (segmentId: string) => {
    if (segmentId === "welcome") {
      navigate("/");
      return;
    }
    if (segmentId === "review") {
      setParams({ step: "review" });
      return;
    }
    setParams({ step: segmentId });
  };

  const question = isReview ? t("wizard.review.title") : t(questionKey(step, answers));

  const optionLabel = (option: QuestionOption): string => {
    const translated = t(option.labelKey);
    if (translated !== option.labelKey) return translated;

    // Keep newly added platform choices readable while a stale locale asset is
    // still cached by a deployment or browser. The locale files remain the
    // source of truth once they are available.
    const fallbackLabels: Record<string, string> = {
      "wizard.steps.rw_version.options.android": "Read&Write for Android",
      "wizard.steps.rw_version.options.ios": "Read&Write for iOS",
      "wizard.steps.tasks_os.options.chromeos": "Browser",
      "wizard.steps.mv_assist_platforms.options.android": "MindView Assist for Android",
      "wizard.steps.mv_assist_platforms.options.ios": "MindView Assist for iOS",
    };
    return fallbackLabels[option.labelKey] ?? translated;
  };

  const renderOptions = () =>
    currentOptions.map((option) => {
      const selected = Array.isArray(currentValue)
        ? currentValue.includes(option.value)
        : currentValue === option.value;
      const tooltip = option.tooltipKey ? t(option.tooltipKey) : undefined;
      const tooltipRw = option.tooltipRwKey ? t(option.tooltipRwKey) : undefined;
      const tooltipMv = option.tooltipMvKey ? t(option.tooltipMvKey) : undefined;
      const showRwTooltip = Boolean(option.tooltipRwKey && answers.products?.includes("rw"));
      const showMvTooltip = Boolean(option.tooltipMvKey && answers.products?.includes("mindview"));

      return (
        <WizardOption
          key={option.value}
          id={step}
          option={option}
          selected={selected}
          multiple={isMultiple}
          label={optionLabel(option)}
          disabled={option.comingSoon}
          tooltip={tooltip}
          tooltipRw={tooltipRw}
          tooltipMv={tooltipMv}
          showRwTooltip={showRwTooltip}
          showMvTooltip={showMvTooltip}
          comingSoonLabel={t("wizard.customerType.education.comingSoon")}
          onSelect={() => updateAnswer(option.value)}
        />
      );
    });

  return (
    <div className="min-h-screen page-bg flex flex-col items-center justify-center px-4 py-12">
      <div className="wizard-surface max-w-xl w-full flex-grow flex flex-col">
        <div className="flex justify-center mb-10">
          <Logo className="h-10 w-auto" />
        </div>

        <div className="flex-grow flex flex-col">
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 flex-grow flex flex-col justify-center">
            <div className="space-y-8">
              <div className="space-y-3 text-center">
                {context ? <QuestionContextBanner context={context} /> : null}
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                  {question}
                </h2>
                {isReview ? (
                  <p className="text-muted-foreground text-base leading-relaxed">
                    {t("wizard.review.subtitle")}
                  </p>
                ) : (
                  <>
                    <p className="text-muted-foreground text-base leading-relaxed">
                      {isMultiple ? t("wizard.selectAll") : t("wizard.selectOne")}
                    </p>
                    {hintKey ? (
                      <p className="text-xs text-amber-300/85 mt-1 italic">{t(hintKey)}</p>
                    ) : null}
                  </>
                )}
              </div>

              {isReview ? (
                <WizardReview visible={visible} answers={answers} formatAnswer={formatAnswer} />
              ) : (
                <div className="space-y-4">
                  {step === "products" ? (
                    <div className="rounded-2xl border border-ink-12 overflow-hidden">
                      <div className="px-5 py-3.5 bg-ink-3 border-b border-ink-8 flex items-center gap-3">
                        <div className="flex items-center gap-2.5 flex-grow">
                          <span className="text-[11px] font-bold text-ink-50 uppercase tracking-[0.14em]">
                            {t("wizard.steps.products.e4wLabel")}
                          </span>
                          <span className="px-2 py-0.5 rounded-full bg-ink-7 border border-ink-10 text-[10px] font-bold text-ink-35 tracking-wider">
                            {t("wizard.steps.products.e4wCode")}
                          </span>
                        </div>
                        <span className="text-[11px] text-ink-25 italic">
                          {t("wizard.steps.products.selectAllHint")}
                        </span>
                      </div>
                      <div className="p-3 space-y-2">{renderOptions()}</div>
                    </div>
                  ) : (
                    <div
                      className={isMultiple ? "space-y-2" : "space-y-3"}
                      role={isMultiple ? "group" : "radiogroup"}
                      aria-label={question}
                    >
                      {renderOptions()}
                    </div>
                  )}
                  {platforms.length > 0 ? (
                    <div className="flex items-center justify-center pt-1">
                      <div className="w-full max-w-full flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-3 py-1.5 rounded-2xl bg-ink-4 border border-ink-8">
                        <span className="shrink-0 whitespace-nowrap text-[10px] font-medium text-ink-30 uppercase tracking-widest">
                          {t("wizard.appliesTo")}
                        </span>
                        <span className="hidden sm:block w-px h-3 bg-ink-10" />
                        <span className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                          {platforms.map((platform, index) => (
                            <span key={platform.value} className="inline-flex items-center gap-1.5 whitespace-nowrap">
                              {index > 0 ? <span className="text-ink-20 text-xs">·</span> : null}
                              <span className="flex items-center gap-1.5">
                                {platform.icons.map((icon) => (
                                  <ProductIcon key={icon} name={icon} className="w-3.5 h-3.5 opacity-50" />
                                ))}
                                <span className="text-[11px] text-ink-40 font-medium">
                                  {platform.label}
                                </span>
                              </span>
                            </span>
                          ))}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          <WizardProgress
            segments={progressSegments}
            current={progressCurrent}
            total={progressTotal}
            onNavigate={navigateProgress}
          />

          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={goBack}
              data-testid="button-wizard-back"
              className="px-5 py-2.5 text-sm font-medium text-muted-foreground rounded-full border border-transparent hover:text-foreground hover:border-ink-15 transition-all duration-200 cursor-pointer flex items-center gap-2"
            >
              <ArrowLeftIcon />
              {t("wizard.buttons.back")}
            </button>
            <button
              type="button"
              data-testid="button-wizard-next"
              disabled={!canContinue}
              onClick={goNext}
              className="px-6 py-2.5 text-sm font-medium text-foreground rounded-full border border-ink-30 bg-transparent hover:bg-ink-10 hover:border-ink-50 transition-all duration-200 cursor-pointer flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-ink-30"
            >
              {isReview ? (
                <>
                  {t("wizard.buttons.generateReport")}
                  <ArrowRightIcon />
                </>
              ) : (
                <>
                  {t("wizard.buttons.next")}
                  <ArrowRightIcon />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
